import { useState, useCallback, FormEvent, ChangeEvent } from "react";
import "./Contact.css";

interface FormState {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const initialForm: FormState = {
  name: "",
  email: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState<"idle" | "success" | "error">("idle");

  const validate = useCallback((values: FormState): FormErrors => {
    const next: FormErrors = {};

    // 名前: 必須 / 2〜50文字
    const name = values.name.trim();
    if (!name) next.name = "名前は必須です";
    else if (name.length < 2) next.name = "2文字以上で入力してください";
    else if (name.length > 50) next.name = "50文字以内で入力してください";

    // メール: 必須 / email 形式
    const email = values.email.trim();
    if (!email) next.email = "メールアドレスは必須です";
    else {
      // シンプルなバリデーション（HTML5と併用）
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(email))
        next.email = "メールアドレスの形式が正しくありません";
    }

    // 本文: 必須 / 10〜2000文字
    const message = values.message.trim();
    if (!message) next.message = "本文は必須です";
    else if (message.length < 10) next.message = "10文字以上で入力してください";
    else if (message.length > 2000)
      next.message = "2000文字以内で入力してください";

    return next;
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setSent("idle");
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setSubmitting(true);
    setSent("idle");

    try {
      const API_URL =
        "https://nw5gbrcacm3j5dgh.devenvkd3caapx.com/wp-json/comodo/v1/contact";
      const FORM_KEY = "change-me-32chars-min"; // 環境変数に置くのが理想

      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Form-Key": FORM_KEY,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed to send");
      setSent("success");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      setSent("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-card">
        <header className="contact-header">
          <h1 className="contact-title">ご意見箱・お問い合わせ</h1>
          <p className="contact-desc">
            お返事に数営業日いただく場合があります。必須項目（*）をご入力ください。
          </p>
        </header>

        {sent === "success" && (
          <div role="status" aria-live="polite" className="alert success">
            送信が完了しました。ご入力ありがとうございます。
          </div>
        )}
        {sent === "error" && (
          <div role="alert" aria-live="assertive" className="alert error">
            送信に失敗しました。時間をおいて再度お試しください。
          </div>
        )}

        <form noValidate onSubmit={handleSubmit} className="form">
          {/* 名前 */}
          <div className="field">
            <label htmlFor="name" className="label">
              お名前<span className="req">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={handleChange}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "name-error" : undefined}
              className="input"
              placeholder="絵文字 太郎"
            />
            {errors.name && (
              <p id="name-error" className="error-text">
                {errors.name}
              </p>
            )}
          </div>

          {/* メール */}
          <div className="field">
            <label htmlFor="email" className="label">
              メールアドレス<span className="req">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={form.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
              className="input"
              placeholder="emoji@example.com"
            />
            {errors.email && (
              <p id="email-error" className="error-text">
                {errors.email}
              </p>
            )}
          </div>

          {/* 本文 */}
          <div className="field">
            <label htmlFor="message" className="label">
              本文<span className="req">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              rows={6}
              value={form.message}
              onChange={handleChange}
              aria-invalid={!!errors.message}
              aria-describedby={errors.message ? "message-error" : undefined}
              className="textarea"
              placeholder="ご意見をご記入ください"
            />
            {errors.message && (
              <p id="message-error" className="error-text">
                {errors.message}
              </p>
            )}
          </div>

          {/* 送信ボタン */}
          <div className="actions">
            <button type="submit" disabled={submitting} className="button">
              {submitting ? "送信中…" : "送信"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
